import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "afripantry_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[cart] could not parse stored cart, resetting:", err);
      return [];
    }
  });
  const [postcode, setPostcode] = useState(() => localStorage.getItem("afripantry_postcode") || "");

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[cart] could not persist cart:", err);
    }
  }, [items]);

  useEffect(() => {
    if (postcode) localStorage.setItem("afripantry_postcode", postcode);
  }, [postcode]);

  const add = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.product_id);
      if (found) {
        return prev.map((i) =>
          i.product_id === product.product_id
            ? { ...i, quantity: Math.min(i.quantity + quantity, product.stock_quantity) }
            : i,
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          image: (product.images || [])[0] || "",
          vendor_id: product.vendor_id,
          vendor_name: product.vendor_name,
          quantity,
          stock_quantity: product.stock_quantity,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((productId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const remove = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCount = useMemo(() => items.reduce((a, i) => a + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((a, i) => a + i.price * i.quantity, 0), [items]);

  const groupedByVendor = useMemo(() => {
    const grouped = items.reduce((acc, i) => {
      const key = i.vendor_id;
      if (!acc[key]) acc[key] = { vendor_id: i.vendor_id, vendor_name: i.vendor_name, items: [], subtotal: 0 };
      acc[key].items.push(i);
      acc[key].subtotal += i.price * i.quantity;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [items]);

  const value = useMemo(
    () => ({ items, postcode, setPostcode, add, updateQty, remove, clear, totalCount, subtotal, groupedByVendor }),
    [items, postcode, add, updateQty, remove, clear, totalCount, subtotal, groupedByVendor],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
